import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MailOutlined,
  StarFilled,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DATE_TIME_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { camelToWords, formatDate, snakeToCamelCase } from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { DisplayComponent } from "components/formItems";
import DisplayEmail from "components/formItems/displayEmail";
import { useUserType } from "features/auth";
import {
  useCreateDebtorEmailMutation,
  useDeleteDebtorEmailMutation,
  useReplicatePacketedDebtorEmailsMutation,
  useUpdateDebtorEmailMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import moment from "moment-timezone";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useCompactMode } from "providers/compactModeProvider";
import {
  ConsentDot,
  ConsentItem,
  CONSENT_STATUSES,
  getConsentColor,
  getStatusTagColor,
  toConsentValue,
} from "./compactCardUtils";

const StyledCard = styled.div`
  margin-top: 0px;
  margin-left: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledHeaderText = styled.h4`
  margin: 2px 0;
`;

const StyledTag = styled(Tag)`
  margin-right: 0;
  font-weight: normal;
`;

const StyledCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  min-width: 0;

  .ant-btn-link {
    padding: 0;
    min-width: auto;
    height: auto;
    line-height: 1;
  }
`;

const StyledPrimaryGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const StyledCompactPrimary = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledStarFilled = styled(StarFilled)`
  color: #faad14;
  flex-shrink: 0;
`;

const StyledTrailingActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 4px;
  }
`;

const StyledLeadingIcon = styled(MailOutlined)`
  color: #8c8c8c;
  flex-shrink: 0;
`;

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: #1677ff;
  cursor: pointer;
`;

const StyledDescriptions = styled(Descriptions)`
  white-space: nowrap;
`;

export function DebtorEmailInfoCard({
  triggerReplicatePacketedDebtorUpdatesModal = () => {},
  email = undefined,
  isNew = undefined,
  onCreate = undefined,
  onCancel = undefined,
  title = undefined,
  isFirstItem = true,
}) {
  const { isAgencyUserType } = useUserType();
  const { isCompactMode } = useCompactMode();
  const [form] = Form.useForm();
  const { debtorId } = useParams();
  const [isEditing, setIsEditing] = useState(isNew || false);
  const { data: constants } = useFetchBackendConstantsQuery();
  const [createDebtorEmail, { isLoading: isCreateLoading }] = useCreateDebtorEmailMutation();
  const [updateDebtorEmail, { isLoading: isUpdateLoading }] = useUpdateDebtorEmailMutation();
  const [deleteDebtorEmail, { isLoading: isDeleteLoading }] = useDeleteDebtorEmailMutation();
  const [replicatePacketedDebtorEmails] = useReplicatePacketedDebtorEmailsMutation();

  // Auto sets consent status change date when status value changes to "consent"
  const onConsentStatusChange = useCallback(
    (value) => {
      if (
        value === "consent" &&
        // The intention here isis: if the user touched the value manually
        // (excluding when using the clearing buttn)
        // we don't want to mess with user selected values.
        (!form.isFieldTouched("consentStatusChangeDate") ||
          !form.getFieldValue("consentStatusChangeDate"))
      ) {
        form.setFields([
          // setFieldValue would set touched to true, use this to avoid that.
          {
            name: "consentStatusChangeDate",
            value: formatDate(moment().endOf("day")),
            touched: false,
          },
        ]);
      }
    },
    [form],
  );

  const {
    data: {
      [PERMISSIONS.DEBTOR_EMAIL__CREATE]: isAddDebtorEmailAuthorized,
      [PERMISSIONS.DEBTOR_EMAIL__UPDATE]: isUpdateDebtorEmailAuthorized,
      [PERMISSIONS.DEBTOR_EMAIL__DELETE]: isDeleteDebtorEmailAuthorized,
    },
  } = useAuthorizations();

  const editableDataSourceTypeValues = constants?.editableDataSourceTypes?.map(
    ({ value }) => value,
  );

  const deleteEmail = async () => {
    const result = await deleteDebtorEmail({ debtorId, emailId: email.id });
    if ("data" in result) {
      message.success("Email deleted successfully!");
    }
  };

  const toggleIsEditing = async () => {
    if (isEditing) {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return null; // Form will handle error messages
      }

      const action = isNew
        ? createDebtorEmail({
            debtorId,
            ...values,
          })
        : updateDebtorEmail({
            debtorId,
            emailId: email.id,
            ...values,
          });
      const result = await action;

      if ("data" in result) {
        message.success("Saved successfully!");
        if (values.isPrimary) {
          triggerReplicatePacketedDebtorUpdatesModal({
            action: replicatePacketedDebtorEmails,
            dataId: result.data.id,
            newData: result.data.address,
            type: "email",
          });
        }
        if (isNew) onCreate();
      }

      if ("error" in result) {
        form.setFields(formatError(result.error));
        message.error(`Could not ${isNew ? "create" : "update"} email`);
        return;
      }
      setIsEditing(false);
    } else {
      setIsEditing(true);
      form.setFieldsValue(email);
    }
  };

  const onPrimaryCancel = () => {
    setIsEditing(false);
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 20 },
      sm: { span: 12 },
    },
  };

  return (
    <StyledCard>
      {(isFirstItem || isNew || isEditing || !isCompactMode) && (
        <StyledHeader>
          {(isFirstItem || isNew || isEditing || !isCompactMode) && (
            <StyledHeaderText>
              <Space>
                {isNew ? "New Email" : title}
                {!isCompactMode && email?.isPrimary && !isEditing && (
                  <StyledTag color="gold">PRIMARY</StyledTag>
                )}
              </Space>
            </StyledHeaderText>
          )}
          {!isCompactMode &&
            !isNew &&
            !isEditing &&
            isAgencyUserType &&
            (isUpdateDebtorEmailAuthorized || isDeleteDebtorEmailAuthorized) && (
              <Row>
                {isUpdateDebtorEmailAuthorized && (
                  <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                )}
                {isDeleteDebtorEmailAuthorized && (
                  <Popconfirm
                    placement="left"
                    okText="Yes"
                    title="Are you sure you want to delete this email?"
                    onConfirm={deleteEmail}
                  >
                    <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Row>
            )}
        </StyledHeader>
      )}
      {!isEditing && isCompactMode && (
        <StyledCompactRow>
          <StyledLeadingIcon />
          <StyledPrimaryGroup>
            <Tooltip
              title={
                email.address +
                (email.type ? ` (${camelToWords(snakeToCamelCase(email.type))})` : "")
              }
            >
              <StyledCompactPrimary>
                {email.address}
                {email.type && ` (${camelToWords(snakeToCamelCase(email.type))})`}
              </StyledCompactPrimary>
            </Tooltip>
            {email.isPrimary && (
              <Tooltip title="Primary">
                <StyledStarFilled />
              </Tooltip>
            )}
          </StyledPrimaryGroup>
          {email.status && (
            <StyledTag color={getStatusTagColor(email.status)}>
              {camelToWords(snakeToCamelCase(email.status))}
            </StyledTag>
          )}
          {CONSENT_STATUSES.includes(email.consentStatus) && (
            <Tooltip
              title={`Email Consent: ${camelToWords(snakeToCamelCase(email.consentStatus))}`}
            >
              <ConsentItem>
                <ConsentDot $color={getConsentColor(toConsentValue(email.consentStatus))} />
                Email
              </ConsentItem>
            </Tooltip>
          )}
          <StyledTrailingActions>
            <Popover
              trigger="hover"
              placement="top"
              content={
                <StyledDescriptions size="small" column={1} bordered>
                  {email.type ? (
                    <Descriptions.Item label="Type">
                      {camelToWords(snakeToCamelCase(email.type))}
                    </Descriptions.Item>
                  ) : null}
                  {email.isPrimary ? (
                    <Descriptions.Item label="Primary">Yes</Descriptions.Item>
                  ) : null}
                  {email.status ? (
                    <Descriptions.Item label="Status">
                      {camelToWords(snakeToCamelCase(email.status))}
                    </Descriptions.Item>
                  ) : null}
                  {email.consentStatus ? (
                    <Descriptions.Item label="Consent">
                      {camelToWords(snakeToCamelCase(email.consentStatus))}
                      {email.consentStatusChangeDate &&
                        ` (${formatDate(email.consentStatusChangeDate)})`}
                    </Descriptions.Item>
                  ) : null}
                  {email.dataSource ? (
                    <Descriptions.Item label="Data Source">
                      {camelToWords(snakeToCamelCase(email.dataSource))}
                    </Descriptions.Item>
                  ) : null}
                  {email.createdAt ? (
                    <Descriptions.Item label="Created At">
                      {moment(email.createdAt).format(DATE_TIME_FORMAT)}
                    </Descriptions.Item>
                  ) : null}
                  {email.notes ? (
                    <Descriptions.Item label="Notes">{email.notes}</Descriptions.Item>
                  ) : null}
                </StyledDescriptions>
              }
            >
              <StyledInfoIcon />
            </Popover>
            {isAgencyUserType &&
              (isUpdateDebtorEmailAuthorized || isDeleteDebtorEmailAuthorized) && (
                <>
                  {isUpdateDebtorEmailAuthorized && (
                    <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                  )}
                  {isDeleteDebtorEmailAuthorized && (
                    <Popconfirm
                      placement="left"
                      okText="Yes"
                      title="Are you sure you want to delete this email?"
                      onConfirm={deleteEmail}
                    >
                      <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </>
              )}
          </StyledTrailingActions>
        </StyledCompactRow>
      )}
      {(!isCompactMode || isEditing) && (
        <StyledForm
          labelAlign="left"
          form={form}
          {...formItemLayout}
          initialValues={{ dataSource: "", status: "", consentStatus: "unknown" }}
        >
          <DisplayComponent
            isEditing={isEditing}
            slice={email}
            label="Primary"
            name="isPrimary"
            valuePropName="checked"
            hidden={email?.isPrimary}
          >
            <Checkbox />
          </DisplayComponent>
          <DisplayEmail
            isEditing={isEditing}
            slice={email}
            label="Email Address"
            name="address"
            rules={[{ required: true, message: "Address is required" }]}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={email}
            label="Type"
            name="type"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
            rules={[{ required: true, message: "Type is required" }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              popupMatchSelectWidth={false}
              options={constants?.emailTypes.map(({ display, value }) => ({
                value,
                label: display,
              }))}
            />
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={email}
            label="Status"
            name="status"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              popupMatchSelectWidth={false}
              options={constants?.emailAddressStatuses.map(({ display, value }) => ({
                value,
                label: display,
              }))}
            />
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={email}
            label="Consent Status"
            name="consentStatus"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
            rules={[{ required: true, message: "Consent Status is required" }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              popupMatchSelectWidth={false}
              onChange={onConsentStatusChange}
              options={constants?.contactPermissionStatuses.map(({ display, value }) => ({
                value,
                label: display,
              }))}
            />
          </DisplayComponent>
          <DisplayComponent
            isEditing={isEditing}
            slice={email}
            label="Consent Date"
            name="consentStatusChangeDate"
            outputFormatter={formatDate}
          >
            <AktDatePicker />
          </DisplayComponent>
          {isEditing && (
            <>
              <DisplayComponent isEditing={isEditing} slice={email} label="Notes" name="notes">
                <Input.TextArea placeholder="Add Notes" />
              </DisplayComponent>
              {/* Only allow setting of data source if no data source is set, or it is editable (i.e. present in the editableDataSourceTypes) */}
              {(!email?.dataSource || editableDataSourceTypeValues.includes(email?.dataSource)) && (
                <DisplayComponent
                  isEditing={isEditing}
                  slice={email}
                  label="Data Source"
                  name="dataSource"
                  outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    allowClear
                    popupMatchSelectWidth={false}
                    options={constants?.editableDataSourceTypes?.map(({ display, value }) => ({
                      value,
                      label: display,
                    }))}
                  />
                </DisplayComponent>
              )}
              <StyledButtonContainer>
                <Space>
                  <Button onClick={isNew ? onCancel : onPrimaryCancel}>Cancel</Button>
                  {(isAddDebtorEmailAuthorized || isUpdateDebtorEmailAuthorized) && (
                    <Button
                      loading={isCreateLoading || isUpdateLoading}
                      onClick={toggleIsEditing}
                      type="primary"
                    >
                      Save
                    </Button>
                  )}
                </Space>
              </StyledButtonContainer>
            </>
          )}
          {!isEditing && (
            <>
              <DisplayComponent
                isEditing={isEditing}
                slice={email}
                label="Data Source"
                name="dataSource"
                outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
              />
              <DisplayComponent
                isEditing={isEditing}
                slice={email}
                label="Created At"
                name="createdAt"
                outputFormatter={(value) => moment(value).format(DATE_TIME_FORMAT)}
              />
            </>
          )}
        </StyledForm>
      )}
    </StyledCard>
  );
}
